import { Box, Typography, Chip } from "@mui/joy";
import { Download, FileText, Image as ImageIcon, Trash2 } from "lucide-react";

import { IconButton } from "../../../../../../shared";
import { AuthenticatedImage } from "../../../../../AuthenticatedImage";
import { describeAttachment } from "./utils";

import type { IProjectAsset } from "../../../../../../interfaces";

interface IAttachmentRowProps {
    asset: IProjectAsset;
    onDownload: () => void;
    onDelete: () => void;
}

export default function AttachmentRow({ asset, onDownload, onDelete }: IAttachmentRowProps) {
    const isPhoto = asset.Kind === "PHOTO";

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: { xs: "40px minmax(0, 1fr) auto", md: "48px minmax(0, 1fr) auto auto" },
                alignItems: "center",
                gap: 1.5,
                py: 1.75,
                borderBottom: "1px solid",
                borderColor: "divider",
            }}
        >
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    backgroundColor: isPhoto
                        ? "rgba(var(--standard-palette-primary-mainChannel) / 0.08)"
                        : "rgba(var(--standard-palette-neutral-mainChannel) / 0.08)",
                    color: isPhoto ? "primary.solidBg" : "neutral.solidBg",
                }}
            >
                {isPhoto && asset.ThumbAssetId ? (
                    <AuthenticatedImage
                        assetId={asset.ThumbAssetId}
                        alt={asset.Title || "Attachment"}
                        width={40}
                        height={40}
                        borderRadius={0}
                        sx={{ objectFit: "cover" }}
                    />
                ) : isPhoto ? (
                    <ImageIcon size={20} />
                ) : (
                    <FileText size={20} />
                )}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography level="body-md" sx={{ fontWeight: 600 }} noWrap>
                    {asset.Title || "Untitled"}
                </Typography>
                <Typography level="body-sm" color="neutral" noWrap>
                    {describeAttachment(asset)}
                </Typography>
            </Box>
            <Chip
                size="sm"
                variant="soft"
                color={isPhoto ? "primary" : "neutral"}
                sx={{ display: { xs: "none", md: "inline-flex" }, justifySelf: "center" }}
            >
                {isPhoto ? "Photo" : "Document"}
            </Chip>
            <Box sx={{ display: "flex", gap: 0.5, justifySelf: "end" }}>
                <IconButton size="sm" variant="plain" color="neutral" title="Download" onClick={onDownload}>
                    <Download size={16} />
                </IconButton>
                <IconButton size="sm" variant="plain" color="danger" title="Delete" onClick={onDelete}>
                    <Trash2 size={16} />
                </IconButton>
            </Box>
        </Box>
    );
}
